import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, CheckCircle2, Clock, XCircle, TrendingUp, Calendar, AlertTriangle, Activity, HeartPulse } from 'lucide-react';
import api from '@/lib/axios';

/**
 * Phase G2: Dashboard-Widget für Probestunden-SMS + Bird-Delivery-Status.
 * Rendert sich nur wenn:
 *   - total_sent > 0 (bereits versendete SMS in Fenster) ODER
 *   - upcoming_trial_lessons > 0 (Probestunden in nächsten 7 Tagen)
 *
 * Delivery-Rate-Semantik: delivered / (delivered + pending + failed).
 * „pending" zählt mit, damit die Quote NICHT künstlich steigt solange der
 * Bird-Webhook noch aussteht. Für reine Ankommens-Quote wäre
 * delivered/(delivered+failed) sinnvoller — dann aber erst nach ≥2 Min warten.
 */
export default function SmsDeliveryWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/trial-lessons/sms-delivery-stats', { params: { days: 7 } })
      .then((r) => setStats(r.data))
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn('[sms-delivery-widget] load failed:', e?.response?.status || e);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading || !stats) return null;
  if (stats.total_sent === 0 && stats.upcoming_trial_lessons === 0 && (!stats.health || stats.health.alert_level === 'ok')) return null;

  const ratePct = stats.total_sent > 0 ? Math.round(stats.delivery_rate * 100) : null;
  const rateColor =
    ratePct === null ? 'text-slate-400' :
    ratePct >= 90 ? 'text-emerald-700' :
    ratePct >= 60 ? 'text-slate-600' : 'text-brand-red';

  const health = stats.health || { alert_level: 'ok', alert_reasons: [] };
  const healthDot = {
    ok: 'bg-emerald-500',
    warning: 'bg-brand-red',
    critical: 'bg-rose-500 animate-pulse',
  }[health.alert_level] || 'bg-slate-400';
  const healthLabel = {
    ok: 'Bird Sender gesund',
    warning: 'Bird Sender: Warnung',
    critical: 'Bird Sender: KRITISCH',
  }[health.alert_level] || '—';

  return (
    <>
      {/* CRITICAL-Banner (nur bei alert_level=critical) */}
      {health.alert_level === 'critical' && (
        <div
          className="mb-4 p-4 rounded-xl border-2 border-rose-400 bg-rose-50 flex items-start gap-3 shadow-sm"
          data-testid="sms-health-critical-banner"
          role="alert"
        >
          <AlertTriangle className="w-6 h-6 text-rose-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-rose-900 mb-1">
              Bird-SMS-Versand kritisch
            </div>
            <ul className="text-xs text-rose-900 space-y-0.5">
              {(health.alert_reasons || []).map((r, i) => <li key={i}>• {r}</li>)}
            </ul>
            <div className="text-xs text-rose-700 mt-2">
              Prüfe Bird-Dashboard (Numbers, Throttling, Access-Key) — Eltern-Erinnerungen gehen möglicherweise gerade NICHT raus.
            </div>
          </div>
        </div>
      )}
      <Link
        to="/trial-lessons"
        className="group block mb-6 bg-white border border-brand-border rounded-xl shadow-sm hover:shadow-md transition-all p-6 hover:border-brand-red/30"
        data-testid="sms-delivery-widget"
      >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brand-red/10 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-brand-red" strokeWidth={1.8} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              Probestunden diese Woche · SMS-Erinnerungen
              <span
                className={`inline-block w-2.5 h-2.5 rounded-full ${healthDot}`}
                title={healthLabel}
                data-testid="sms-health-dot"
                data-health-level={health.alert_level}
              />
            </h3>
            <p className="text-xs text-slate-500">
              Aktueller Mandant · Fenster: letzte {stats.window_days} Tage
              {stats.last_webhook_at && (
                <span> · letzte Bird-Rückmeldung: {new Date(stats.last_webhook_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</span>
              )}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-3xl font-bold tabular-nums ${rateColor}`} data-testid="sms-delivery-rate">
            {ratePct === null ? '—' : `${ratePct}%`}
          </div>
          <div className="text-xs text-slate-500" title="Zugestellt geteilt durch (Zugestellt + Unterwegs + Fehler)">
            Zustellquote
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white/70 rounded-lg p-3 border border-slate-200" data-testid="sms-stat-upcoming">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-brand-red" />
            <span className="text-xs font-medium text-slate-600">Bevorstehend (7 Tage)</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 tabular-nums">{stats.upcoming_trial_lessons}</div>
        </div>
        <div className="bg-white/70 rounded-lg p-3 border border-slate-200" data-testid="sms-stat-delivered">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="text-xs font-medium text-slate-600">Zugestellt</span>
          </div>
          <div className="text-2xl font-bold text-emerald-700 tabular-nums">{stats.delivered}</div>
        </div>
        <div className="bg-white/70 rounded-lg p-3 border border-slate-200" data-testid="sms-stat-pending">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-brand-red" />
            <span className="text-xs font-medium text-slate-600">Unterwegs</span>
          </div>
          <div className="text-2xl font-bold text-slate-700 tabular-nums">{stats.pending}</div>
        </div>
        <div className="bg-white/70 rounded-lg p-3 border border-slate-200" data-testid="sms-stat-failed">
          <div className="flex items-center gap-2 mb-1">
            <XCircle className={`w-4 h-4 ${stats.failed > 0 ? 'text-rose-600' : 'text-slate-400'}`} />
            <span className="text-xs font-medium text-slate-600">Fehler</span>
          </div>
          <div className={`text-2xl font-bold tabular-nums ${stats.failed > 0 ? 'text-rose-700' : 'text-slate-400'}`}>{stats.failed}</div>
        </div>
      </div>
      {stats.total_sent > 0 && (
        <div className="mt-3 flex items-center gap-2 text-xs">
          <TrendingUp className="w-3 h-3 text-sky-600" />
          <span className="text-slate-600">
            {stats.total_sent} SMS insgesamt
            {stats.throttled > 0 && ` · ${stats.throttled} dedupe-geblockt`}
          </span>
          <span className="ml-auto text-sky-700 font-medium group-hover:underline">zur Liste →</span>
        </div>
      )}

      {/* Bird-Health-Probe-Info (kompakt) */}
      {health.last_probe_at && (
        <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-2 text-xs text-slate-500" data-testid="sms-health-info">
          <HeartPulse className="w-3 h-3 text-rose-500" />
          <span>
            Bird-Sender-Probe: {health.last_probe_status === 'delivered' ? '✓ zugestellt' :
              health.last_probe_status === 'sent' ? '→ unterwegs' :
              health.last_probe_status === 'failed' ? '✗ fehlgeschlagen' : health.last_probe_status}
            {' · '}
            {new Date(health.last_probe_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
          </span>
          {health.realworld_samples_7d >= 5 && health.realworld_delivery_rate_7d !== null && (
            <span className="ml-auto">
              7-Tage-Quote: <strong>{Math.round(health.realworld_delivery_rate_7d * 100)}%</strong>
              {' '}({health.realworld_samples_7d} Samples)
            </span>
          )}
        </div>
      )}
      </Link>
    </>
  );
}
